import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  UserGift,
  GiftPicture,
  GiftByCodeResponse,
} from "../../types/gifts";
import type { City } from "../../types";

interface GiftsState {
  // Список моих подарков
  myGifts: UserGift[];
  myGiftsLoading: boolean;

  // Текущий просматриваемый подарок (по коду)
  currentGift: GiftByCodeResponse | null;
  currentGiftLoading: boolean;

  // Список доступных картинок
  pictures: GiftPicture[];
  picturesLoading: boolean;

  // Список городов
  cities: City[];
  citiesLoading: boolean;

  // UI состояния
  showCreateGiftModal: boolean;
  showActivateGiftModal: boolean;

  // Ошибки
  error: string | null;
}

const initialState: GiftsState = {
  myGifts: [],
  myGiftsLoading: false,

  currentGift: null,
  currentGiftLoading: false,

  pictures: [],
  picturesLoading: false,

  cities: [],
  citiesLoading: false,

  showCreateGiftModal: false,
  showActivateGiftModal: false,

  error: null,
};

const giftsSlice = createSlice({
  name: "gifts",
  initialState,
  reducers: {
    // Мои подарки
    setMyGifts: (state, action: PayloadAction<UserGift[]>) => {
      state.myGifts = action.payload;
    },
    setMyGiftsLoading: (state, action: PayloadAction<boolean>) => {
      state.myGiftsLoading = action.payload;
    },
    addGift: (state, action: PayloadAction<UserGift>) => {
      state.myGifts.push(action.payload);
    },
    updateGift: (state, action: PayloadAction<UserGift>) => {
      const index = state.myGifts.findIndex((g) => g.id === action.payload.id);
      if (index !== -1) {
        state.myGifts[index] = action.payload;
      }
    },

    // Текущий подарок
    setCurrentGift: (state, action: PayloadAction<GiftByCodeResponse | null>) => {
      state.currentGift = action.payload;
    },
    setCurrentGiftLoading: (state, action: PayloadAction<boolean>) => {
      state.currentGiftLoading = action.payload;
    },

    // Картинки
    setPictures: (state, action: PayloadAction<GiftPicture[]>) => {
      state.pictures = action.payload;
    },
    setPicturesLoading: (state, action: PayloadAction<boolean>) => {
      state.picturesLoading = action.payload;
    },

    // Города
    setCities: (state, action: PayloadAction<City[]>) => {
      state.cities = action.payload;
    },
    setCitiesLoading: (state, action: PayloadAction<boolean>) => {
      state.citiesLoading = action.payload;
    },

    // UI
    setShowCreateGiftModal: (state, action: PayloadAction<boolean>) => {
      state.showCreateGiftModal = action.payload;
    },
    setShowActivateGiftModal: (state, action: PayloadAction<boolean>) => {
      state.showActivateGiftModal = action.payload;
    },

    // Ошибки
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Очистка
    clearGifts: (state) => {
      state.myGifts = [];
      state.currentGift = null;
      state.error = null;
    },
  },
});

export const {
  setMyGifts,
  setMyGiftsLoading,
  addGift,
  updateGift,
  setCurrentGift,
  setCurrentGiftLoading,
  setPictures,
  setPicturesLoading,
  setCities,
  setCitiesLoading,
  setShowCreateGiftModal,
  setShowActivateGiftModal,
  setError,
  clearGifts,
} = giftsSlice.actions;

export const giftsReducer = giftsSlice.reducer;
